import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, TrendingUp, CheckCircle2, XCircle, Wallet, ClipboardList, CupSoda, Bike, Percent } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, LineChart, Line } from "recharts";
import { api, brl, errMsg, PLATFORMS } from "@/lib/api";
import { PageHeader, Panel } from "@/components/Shared";
import { inputCls, btnGhost } from "@/components/Form";
import PlatformSummary from "@/components/PlatformSummary";

const BAR_RADIUS = [4, 4, 0, 0];

const Kpi = ({ label, value, icon: Icon, accent, testid }) => (
  <div data-testid={testid} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
    <div className="flex items-center justify-between"><p className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</p><Icon className={`h-5 w-5 ${accent || "text-slate-400"}`} /></div>
    <p className="mt-3 font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">{value}</p>
  </div>
);

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [f, setF] = useState({ date_from: "", date_to: "", platform: "" });

  useEffect(() => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    api.get("/dashboard", { params }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [f]);

  const exportCsv = async () => {
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
      const r = await api.get("/cases/export/csv", { params, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data); a.download = "recursofood-recursos.csv"; a.click();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="fade-up">
      <PageHeader eyebrow="Painel" title="Dashboard financeiro" desc="Valores recuperados só são contabilizados após aprovação confirmada pela plataforma.">
        <button data-testid="export-csv-btn" onClick={exportCsv} className={btnGhost}><Download className="h-4 w-4" /> Exportar CSV</button>
      </PageHeader>
      <div className="mb-6 grid gap-3 sm:grid-cols-3">
        <input data-testid="dash-date-from" type="date" className={inputCls} value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} />
        <input data-testid="dash-date-to" type="date" className={inputCls} value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} />
        <select data-testid="dash-platform-filter" className={inputCls} value={f.platform} onChange={(e) => setF({ ...f, platform: e.target.value })}>
          <option value="">Todas as plataformas</option>{Object.entries(PLATFORMS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      {!d ? <div className="h-64 animate-pulse rounded-xl bg-slate-200" /> : (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div data-testid="kpi-recovered" className="rounded-xl bg-slate-900 p-5 text-white shadow-sm sm:col-span-2">
              <p className="text-xs font-bold uppercase tracking-wider text-amber-400">Valores recuperados (confirmados)</p>
              <p className="mt-2 font-display text-4xl font-extrabold sm:text-5xl">{brl(d.recovered_value)}</p>
              <p className="mt-2 text-sm text-slate-400">de {brl(d.contested_value)} contestados</p>
            </div>
            <Kpi testid="kpi-approval-rate" label="Taxa de aprovação" value={`${d.approval_rate}%`} icon={Percent} accent="text-emerald-600" />
            <Kpi testid="kpi-total-reviews" label="Avaliações analisadas" value={d.total_reviews} icon={ClipboardList} />
            <Kpi testid="kpi-cancel" label="Cancelamentos contestados" value={d.cancel_contested} icon={TrendingUp} />
            <Kpi testid="kpi-refund" label="Reembolsos contestados" value={d.refund_contested} icon={Wallet} />
            <Kpi testid="kpi-approved" label="Recursos aprovados" value={d.approved} icon={CheckCircle2} accent="text-emerald-600" />
            <Kpi testid="kpi-denied" label="Recursos negados" value={d.denied} icon={XCircle} accent="text-red-600" />
            <Kpi testid="kpi-drink" label="Refrigerante/bebida ausente" value={d.drink_missing} icon={CupSoda} accent="text-amber-600" />
            <Kpi testid="kpi-delivery" label="Atribuídos à entrega" value={d.delivery_issues} icon={Bike} accent="text-amber-600" />
          </div>
          <PlatformSummary items={d.by_platform} platform={f.platform} />
          <div className="grid gap-6 lg:grid-cols-2">
            <Panel title="Evolução mensal dos resultados" testid="chart-monthly">
              <div className="h-72"><ResponsiveContainer><BarChart data={d.monthly}><CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" /><XAxis dataKey="mes" fontSize={12} /><YAxis fontSize={12} allowDecimals={false} /><Tooltip /><Legend />
                <Bar dataKey="total" name="Casos" fill="#063A1C" radius={BAR_RADIUS} /><Bar dataKey="aprovadas" name="Aprovadas" fill="#2F7A5B" radius={BAR_RADIUS} /><Bar dataKey="negadas" name="Negadas" fill="#FF6A0F" radius={BAR_RADIUS} /></BarChart></ResponsiveContainer></div>
            </Panel>
            <Panel title="Valores contestados x recuperados (R$)" testid="chart-values">
              <div className="h-72"><ResponsiveContainer><LineChart data={d.monthly}><CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" /><XAxis dataKey="mes" fontSize={12} /><YAxis fontSize={12} /><Tooltip formatter={(v) => brl(v)} /><Legend />
                <Line dataKey="contestado" name="Contestado" stroke="#D97706" strokeWidth={3} /><Line dataKey="recuperado" name="Recuperado" stroke="#059669" strokeWidth={3} /></LineChart></ResponsiveContainer></div>
            </Panel>
          </div>
          <Panel title="Principais motivos de reclamação" testid="top-motivos">
            {d.top_motivos.length ? <div className="space-y-3">{d.top_motivos.map((m) => (
              <div key={m.motivo}><div className="flex justify-between gap-4 text-sm"><span className="text-slate-700">{m.motivo}</span><b>{m.total}</b></div>
                <div className="mt-1 h-2 rounded-full bg-slate-100"><div className="h-2 rounded-full bg-amber-500" style={{ width: `${(100 * m.total) / d.total_cases}%` }} /></div></div>))}</div>
              : <p className="text-sm text-slate-400">Sem dados no período.</p>}
          </Panel>
        </div>
      )}
    </div>
  );
}
